import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Document } from '../../models/policy';
import {
  CalculateValuationPensionPeriod,
  CalculateValuationResultController,
  CalculateValuationResultView,
  MovementDetail,
  PolicyPension
} from '../../models/policy-pension';
import { BaseService } from '../core/base.service';
import { OperationRegisterService } from '../core/operation-register.service';

@Injectable({
  providedIn: 'root'
})
export class PensionPoliciesService {
  constructor(
    private http: HttpClient,
    private base: BaseService,
    private or: OperationRegisterService
  ) {}

  getPensionPolicy({
    policyNumber,
    productCode
  }: PolicyPension): Observable<PolicyPension> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/pensionInvestmentPolicies/${policyNumber}?productCode=${productCode}`;
    return this.http.get<PolicyPension>(
      url,
      this.base.getPrivateOIMHeaders({ cacheResponse: true })
    );
  }

  getMovement({
    policyNumber,
    productCode,
    number
  }): Observable<MovementDetail> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/pensionInvestmentPolicies/${policyNumber}/movements/${number}?productCode=${productCode}`;
    const params = this.or.setPensionPoliciesOperationParams(policyNumber);
    return this.http.get<MovementDetail>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params
    });
  }

  getPensionPolicyAuditReport(
    policyNumber: string,
    productCode: string
  ): Observable<Document> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/pensionInvestmentPolicies/${policyNumber}/documents/auditReport?productCode=${productCode}`;
    return this.http.get<Document>(
      url,
      this.base.getPrivateOIMHeaders({ cacheResponse: true })
    );
  }

  getPensionPolicyFiscalDuplicate(
    policyNumber: string,
    productCode: string,
    exercise: string,
    params?: HttpParams
  ): Observable<Document> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/pensionInvestmentPolicies/${policyNumber}/documents/fiscalDuplicate?productCode=${productCode}&exercise=${exercise}`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Document>(url, { ...headers, params });
  }

  getPensionPolicyLifeCycleFiscalDuplicate(
    exercise: string,
    params?: HttpParams,
    lifeCycleCode?: string
  ): Observable<Document> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/pensionInvestmentPolicies/lifeCycle/documents/fiscalDuplicate?productCode=${(lifeCycleCode || 'CVID')}&exercise=${exercise}`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Document>(url, { ...headers, params });
  }

  getPensionPolicyQuarterlyReport(
    policy: PolicyPension,
    productCode: string,
    exercise: string,
    period: string,
    params?: HttpParams
  ): Observable<Document> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/pensionInvestmentPolicies/${
      policy.policyNumber
    }/documents/quarterlyReport?productCode=${productCode}&exercise=${exercise}&period=${period}`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Document>(url, {
      ...headers,
      params
    });
  }

  getPensionPolicyPositionStatusReport(
    policy: PolicyPension,
    productCode: string,
    exercise: string,
    period: string,
    params?: HttpParams
  ): Observable<Document> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/pensionInvestmentPolicies/${
      policy.policyNumber
    }/documents/positionStatusReport?productCode=${productCode}&exercise=${exercise}&period=${period}`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Document>(url, { ...headers, params });
  }

  getPensionPolicyLifeCyclePositionStatusReport(
    productCode: string,
    exercise: string,
    period: string,
    params?: HttpParams
  ): Observable<Document> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/pensionInvestmentPolicies/lifeCycle/documents/positionStatusReport?productCode=${productCode}&exercise=${exercise}&period=${period}`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Document>(url, { ...headers, params });
  }

  getPensionPolicyLifeCycleQuarterlyReport(
    productCode: string,
    exercise: string,
    period: string,
    params?: HttpParams
  ): Observable<Document> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/pensionInvestmentPolicies/lifeCycle/documents/quarterlyReport?productCode=${productCode}&exercise=${exercise}&period=${period}`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Document>(url, {
      ...headers,
      params
    });
  }

  getPensionPolicyCalculateValuation(
    dates: CalculateValuationPensionPeriod,
    policy: PolicyPension
  ) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/pensionInvestmentPolicies/${
      policy.policyNumber
    }/funds/${policy.productCode}/valuation`;
    let params = this.or.setPensionPoliciesOperationParams(policy.policyNumber);
    params = params.set('start', dates.initDate);
    params = params.set('end', dates.endDate);
    return this.http
      .get<CalculateValuationResultController>(url, {
        ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
        params
      })
      .pipe(
        map(result => {
          return {
            rangeInDays: result.rangeInDays,
            sections: [
              {
                name: 'liquidation',
                ...result.liquidation
              },
              {
                name: 'patrimony',
                ...result.patrimony
              }
            ]
          } as CalculateValuationResultView;
        })
      );
  }
}
